import { AsyncPipe } from '@angular/common'
import { ChangeDetectionStrategy, Component, inject, Input, signal } from '@angular/core'
import { MatFormField, MatLabel } from '@angular/material/form-field'
import {
  MatCell,
  MatCellDef,
  MatColumnDef,
  MatHeaderCell,
  MatHeaderCellDef,
} from '@angular/material/table'
import {
  ElderBasicPaneLayoutComponent,
  ElderDataToolbarComponent,
  ElderPaneComponent,
  ElderPaneContentComponent,
  ElderPaneHeaderComponent,
  ElderPaneSubtitleComponent,
  ElderSelectComponent,
  ElderTableActivationDirective,
  ElderTableComponent,
  ElderTableToolbarDirective,
  ElderToastService,
  ElderToolbarContentDirective,
  ViewProviders,
} from '@elderbyte/ngx-starter'
import { ElderTranslatePipe } from '@elderbyte/ngx-starter'
import { BehaviorSubject, Observable } from 'rxjs'
import { CapabilitySpecService } from '../../../../../process/capability-spec/capability-spec.service'
import { SkillSetMaterializer } from '../../../../../process/capability-spec/skillsets/skill-set-materializer'
import { SkillSetSpec } from '../../../../../process/capability-spec/skillsets/skill-set-spec'
import { SkillSetConf } from '../../skillsets/skill-set-conf'
import { SkillSetDetailComponent } from '../../skillsets/skill-set-detail/skill-set-detail.component'
import { CapabilityConf } from '../capability-config'

@Component({
  selector: 'app-capability-detail',
  templateUrl: './capability-detail.component.html',
  styleUrls: ['./capability-detail.component.scss'],
  viewProviders: [ViewProviders.ParentControlContainer],
  imports: [
    ElderBasicPaneLayoutComponent,
    ElderPaneComponent,
    ElderPaneHeaderComponent,
    ElderPaneContentComponent,
    ElderPaneSubtitleComponent,
    ElderTableComponent,
    ElderTableActivationDirective,
    ElderTableToolbarDirective,
    ElderDataToolbarComponent,
    ElderToolbarContentDirective,
    MatFormField,
    MatLabel,
    ElderSelectComponent,
    MatColumnDef,
    MatHeaderCellDef,
    MatHeaderCell,
    MatCellDef,
    MatCell,
    SkillSetDetailComponent,
    AsyncPipe,
    ElderTranslatePipe,
  ],
})
export class CapabilityDetailComponent {
  /***************************************************************************
   *                                                                         *
   * Fields                                                                  *
   *                                                                         *
   **************************************************************************/

  private readonly _capability$: BehaviorSubject<CapabilityConf> = new BehaviorSubject(null)

  public readonly skillSets$ = new BehaviorSubject<SkillSetConf[]>([])
  public readonly selectedSkillSet = signal<SkillSetConf>(null)

  public readonly availableSkillSetSpecs = signal<SkillSetSpec[]>([])
  public readonly addableSkillSetSpecs = signal<SkillSetSpec[]>([])

  public dummyValue: SkillSetSpec

  private capabilitySpecService = inject(CapabilitySpecService)
  private toast = inject(ElderToastService)

  /***************************************************************************
   *                                                                         *
   * Getter / Setters                                                        *
   *                                                                         *
   **************************************************************************/

  public get capability$(): Observable<CapabilityConf> {
    return this._capability$.asObservable()
  }

  public get capability(): CapabilityConf {
    return this._capability$.getValue()
  }

  @Input()
  public set capability(capability: CapabilityConf) {
    if (capability) {
      this._capability$.next(capability)
      this.skillSets = capability.skillSets
      this.capabilitySpecService.findById(this.capability.capabilitySpecId).subscribe({
        next: (capabilitySpec) => {
          this.availableSkillSetSpecs.set(capabilitySpec.skillSets)
          this.evaluateAvailableSkillSetSpecs()
          this.autoSelectSkillSet()
        },
        error: (err) => this.toast.error('Failed to load capability spec!', err),
      })
    }
  }

  public set skillSets(skillSets: SkillSetConf[]) {
    this.sortSkillSets(skillSets)
    this.skillSets$.next(skillSets)
    this.capability.skillSets = skillSets
  }

  public get skillSets() {
    return this.skillSets$.getValue()
  }

  /***************************************************************************
   *                                                                         *
   * Component Events                                                        *
   *                                                                         *
   **************************************************************************/

  public onNewSkillSet(spec: SkillSetSpec): void {
    if (spec && this.filterAvailableSkillSetSpecs([spec]).includes(spec)) {
      // Only needed because of the dummy value reset
      const skillSets = [...this.skillSets]
      const skillSetConf = SkillSetMaterializer.start(spec).description('Neu').materialize()

      skillSets.push(skillSetConf)
      this.skillSets = skillSets
      this.selectedSkillSet.set(skillSetConf)
      this.evaluateAvailableSkillSetSpecs()

      this.dummyValue = spec
      setTimeout(() => {
        this.dummyValue = null
      })
    }
  }

  public onRemoveSkillSets(toRemove: SkillSetConf[]): void {
    const skillSets = [...this.skillSets]
    const filtered = toRemove.filter((c) => this.deletionEnabled(c))

    if (filtered.length > 0) {
      filtered.forEach((c) => skillSets.splice(skillSets.indexOf(c), 1))
      this.skillSets = skillSets
      this.selectedSkillSet.set(null)
      this.evaluateAvailableSkillSetSpecs()
    } else {
      this.toast.info('Skill ist erforderlich!') // TODO disable delete button if deletion is not possible
    }
  }

  /***************************************************************************
   *                                                                         *
   * Private Methods                                                         *
   *                                                                         *
   **************************************************************************/

  private deletionEnabled(conf: SkillSetConf): boolean {
    const spec = this.availableSkillSetSpecs().find((sss) => sss.id === conf.skillSetSpecId)
    return (
      !spec?.required ||
      (spec?.required &&
        this.skillSets.filter((c) => c.skillSetSpecId === conf?.skillSetSpecId).length > 1)
    )
  }

  private autoSelectSkillSet(): void {
    const skills = this.skillSets
    if (skills && skills.length > 0) {
      const autoSelected = this.skillSets.find((ss) => ss.id === this.selectedSkillSet()?.id)
      this.selectedSkillSet.set(autoSelected ? autoSelected : skills[0])
    } else {
      this.selectedSkillSet.set(null)
    }
  }

  private filterAvailableSkillSetSpecs(skillSetsToFilter: SkillSetSpec[]): SkillSetSpec[] {
    const currentSpecIds = this.skillSets.map((ssc) => ssc.skillSetSpecId)
    return skillSetsToFilter.filter(
      (sss) => !sss.singular || (sss.singular && !currentSpecIds.includes(sss.id))
    )
  }

  private evaluateAvailableSkillSetSpecs(): void {
    if (this.capability) {
      this.addableSkillSetSpecs.set(
        this.filterAvailableSkillSetSpecs(this.availableSkillSetSpecs())
      )
    }
  }

  private sortSkillSets(skillSets: SkillSetConf[]): void {
    if (skillSets) {
      skillSets.sort(
        (sa, sb) =>
          +!!sb.spec?.singular - +!!sa.spec?.singular || // Singular specs first
          +!!sb.spec?.required - +!!sa.spec?.required || // then required specs
          sa.spec?.name.localeCompare(sb.spec?.name) || // then alphabetically by spec name
          sa.description.localeCompare(sb.description) || // then alphabetically by conf description
          sa.id.localeCompare(sb.id) // lastly by conf id
      )
    }
  }
}
